/**
 * Safe localStorage utilities with error handling
 *
 * Provides JSON get/set operations that gracefully handle:
 * - localStorage being disabled/unavailable
 * - Quota exceeded errors
 * - JSON parse errors
 *
 * Usage:
 * ```
 * import { safeGetJSON, safeSetJSON } from 'datastage/utils/safeLocalStorage';
 *
 * const value = safeGetJSON('myKey', defaultValue);
 * const success = safeSetJSON('myKey', { data: 'value' });
 * ```
 */

/**
 * Safely retrieves and parses a JSON value from localStorage.
 *
 * @param {string} key - localStorage key
 * @param {*} fallback - Value to return if key doesn't exist or parsing fails
 * @returns {*} Parsed JSON value or fallback
 */
export function safeGetJSON(key, fallback = null) {
    try {
        const item = localStorage.getItem(key);
        if (item === null) {
            return fallback;
        }
        return JSON.parse(item);
    } catch (error) {
        console.warn(`Failed to read localStorage key "${key}":`, error);
        return fallback;
    }
}

/**
 * Safely stringifies and stores a value in localStorage.
 *
 * @param {string} key - localStorage key
 * @param {*} value - Value to store (will be JSON.stringify'd)
 * @returns {boolean} true if successful, false otherwise
 */
export function safeSetJSON(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
    } catch (error) {
        console.warn(`Failed to write localStorage key "${key}":`, error);
        return false;
    }
}

/**
 * Safely removes a key from localStorage.
 *
 * @param {string} key - localStorage key to remove
 * @returns {boolean} true if successful, false otherwise
 */
export function safeRemove(key) {
    try {
        localStorage.removeItem(key);
        return true;
    } catch (error) {
        console.warn(`Failed to remove localStorage key "${key}":`, error);
        return false;
    }
}
